import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { useRandomId } from 'js/utils/hooks/useRandomId';

import { Errors } from 'js/design_system/forms/Errors';

import 'app/assets/stylesheets/design_system/form.scss';

export type InputProps = Omit<
  React.HTMLProps<HTMLInputElement>,
  'ref' | 'label' | 'onChange'
> & {
  /** Manually ref type to fix forwardRef usages */
  ref?: React.Ref<HTMLInputElement>;
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** The input's description. Shown directly underneath the label. */
  description?: ReactNode;
  /** The input's supporting text. Shown underneath the input and error messages. */
  support?: ReactNode;
  /** Content to prepend before the actual text input */
  prepend?: ReactNode;
  /** Content to append after the actual text input */
  append?: ReactNode;
  /** The errors to show beneath the input */
  errors?: ReactNode[];
  /** Whether to decorate the input with error styling */
  hasError?: boolean;
  /** Change event for the input */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * # Input
 * An input component for text entry. Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `label`: The input's label. Shown at the top of the component.
 * - `description`: The input's description. Shown directly underneath the label.
 * - `support`: The input's supporting text. Shown underneath the input and error messages.
 * - `hasError`: Whether to decorate the input with error styling
 * - `errors`: The errors to show beneath the input
 * - `disabled`: Whether the input is disabled
 * - `prepend`: Content to prepend before the actual text input. (e.g. a search icon)
 * - `append`: Content to append after the actual text input
 * - `onChange`: Change event for the input
 * - `ref`: A ref that will forwarded specifically to the input element
 * - `style`: Additional styles to apply to the input container
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Input } from 'js/design_system/forms/Input';
 *
 * <Input placeholder="Enter your name..." />
 * ```
 *
 * ## With Error
 * Avoid only using the `hasError` prop to indicate an error.
 * Color change should never be the only indicator of an error.
 * Pass the error message as a prop to the `errors` prop.
 *
 * @example
 *
 * ```tsx
 * import { Input } from 'js/design_system/forms/Input';
 *
 * <Input hasError errors={['Name cannot be blank']} />
 * ```
 *
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      support,
      prepend,
      append,
      errors = [],
      hasError = false,
      disabled,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    // Random ID for ARIA attributes
    const id = useRandomId();

    const inputContainerClasses = classNames(
      className,
      'ds2-5-input--container',
    );

    const inputWrapperClasses = classNames('ds2-5-input--wrapper', {
      'ds2-5-input--error': !disabled && hasError,
      'ds2-5-input--disabled': disabled,
    });

    const labelClasses = label
      ? classNames('ds2-5-form--label', {
          'ds2-5-form--label-disabled': disabled,
        })
      : undefined;

    const descriptionClasses = description
      ? classNames('ds2-5-form--desc', {
          'ds2-5-form--label-disabled': disabled,
        })
      : undefined;

    const supportClasses = support
      ? classNames('ds2-5-input--support', {
          'ds2-5-input--support-disabled': disabled,
        })
      : undefined;

    return (
      <div
        className={inputContainerClasses}
        style={style}
        aria-disabled={disabled}
      >
        {label && (
          <label
            htmlFor={`${id}-input`}
            id={`${id}-label`}
            className={labelClasses}
          >
            {label}
          </label>
        )}
        {description && (
          <div id={`${id}-desc`} className={descriptionClasses}>
            {description}
          </div>
        )}
        <div className={inputWrapperClasses}>
          {prepend}
          <input
            id={`${id}-input`}
            ref={ref}
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-desc ${id}-errors`}
            aria-invalid={!disabled && hasError}
            disabled={disabled}
            {...props}
          />
          {append}
        </div>

        {!disabled && hasError && (
          <Errors id={`${id}-errors`} errors={errors} />
        )}

        {support && <div className={supportClasses}>{support}</div>}
      </div>
    );
  },
);
